import { useState, useRef, useEffect } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CloudArrowDownIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  KeyIcon,
  ClipboardDocumentListIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { backupApi, surveyApi, SurveyPreviewResult, SurveyImportStats, SurveyImportResult, ConflictAction } from '../services/api';
import { BackupData } from '../types';
import { fetchPeople } from '../store/peopleSlice';
import { fetchAssignments } from '../store/assignmentsSlice';
import { fetchInventory, fetchTransactions } from '../store/keyInventorySlice';
import { fetchLocations } from '../store/locationsSlice';
import { fetchKeyTypes } from '../store/keyTypesSlice';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';
import * as XLSX from 'xlsx';

interface ExportOption {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

export default function BackupRestore() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.users.currentUser);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Data for exports
  const people = useAppSelector((state) => state.people.people);
  const assignments = useAppSelector((state) => state.assignments.assignments);
  const inventory = useAppSelector((state) => state.keyInventory.inventory);
  const transactions = useAppSelector((state) => state.keyInventory.transactions);
  const locations = useAppSelector((state) => state.locations.locations);
  const keyTypes = useAppSelector((state) => state.keyTypes.keyTypes);

  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importData, setImportData] = useState<BackupData | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [exportingReport, setExportingReport] = useState<string | null>(null);

  // Survey import state
  const surveyFileInputRef = useRef<HTMLInputElement>(null);
  const [surveyData, setSurveyData] = useState<Record<string, string>[] | null>(null);
  const [surveyPreview, setSurveyPreview] = useState<SurveyPreviewResult | null>(null);
  const [isSurveyPreviewModalOpen, setIsSurveyPreviewModalOpen] = useState(false);
  const [isPreviewingSurvey, setIsPreviewingSurvey] = useState(false);
  const [isImportingSurvey, setIsImportingSurvey] = useState(false);
  const [surveyImportLocationId, setSurveyImportLocationId] = useState<string>('');
  const [surveyImportResults, setSurveyImportResults] = useState<{ stats: SurveyImportStats; results: SurveyImportResult[] } | null>(null);
  const [isSurveyResultsModalOpen, setIsSurveyResultsModalOpen] = useState(false);
  const [conflictResolutions, setConflictResolutions] = useState<Record<string, ConflictAction>>({});
  const [defaultConflictAction, setDefaultConflictAction] = useState<ConflictAction>('merge');

  const isAdmin = currentUser?.role === 'admin';

  // Fetch data on mount for exports
  useEffect(() => {
    dispatch(fetchPeople());
    dispatch(fetchAssignments());
    dispatch(fetchInventory());
    dispatch(fetchTransactions());
    dispatch(fetchLocations());
    dispatch(fetchKeyTypes());
  }, [dispatch]);

  const exportOptions: ExportOption[] = [
    {
      id: 'people-keys',
      name: 'People & Assigned Keys',
      description: 'Export all people with their currently assigned keys',
      icon: UserGroupIcon,
    },
    {
      id: 'inventory',
      name: 'Key Inventory',
      description: 'Export complete key inventory with quantities and locations',
      icon: KeyIcon,
    },
    {
      id: 'transactions',
      name: 'Transaction History',
      description: 'Export all key transactions (assignments, returns, adjustments)',
      icon: ClipboardDocumentListIcon,
    },
    {
      id: 'full-report',
      name: 'Full Report',
      description: 'Export all data in a single workbook with multiple sheets',
      icon: DocumentArrowDownIcon,
    },
  ];

  const getLocationName = (locationId: string) => {
    return locations.find((l) => l.id === locationId)?.name || 'Unknown';
  };

  const getKeyTypeName = (keyTypeId: string) => {
    return keyTypes.find((kt) => kt.id === keyTypeId)?.name || 'Unknown';
  };

  const downloadExcel = (workbook: XLSX.WorkBook, filename: string) => {
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportPeopleAndKeys = () => {
    setExportingReport('people-keys');
    try {
      const activePeople = people.filter((p) => p.status === 'active');
      const activeAssignments = assignments.filter((a) => a.status === 'active');

      const data = activePeople.map((person) => {
        const personAssignments = activeAssignments.filter((a) => a.personId === person.id);
        const assignedKeys = personAssignments.map((a) => {
          const invItem = inventory.find((i) => i.id === a.keyInventoryItemId);
          if (invItem) {
            return `${getKeyTypeName(invItem.keyTypeId)} (${getLocationName(invItem.locationId)}) x${a.quantity}`;
          }
          return 'Unknown Key';
        }).join('; ');

        const totalKeysAssigned = personAssignments.reduce((sum, a) => sum + a.quantity, 0);

        return {
          'Name': person.name,
          'Email': person.email || '',
          'Office Location': person.officeLocation || '',
          'Department': person.department || '',
          'Business Phone': person.businessPhone || '',
          'Mobile Phone': person.mobilePhone || '',
          'Location': person.locationId ? getLocationName(person.locationId) : '',
          'Total Keys Assigned': totalKeysAssigned,
          'Assigned Keys': assignedKeys,
          'Status': person.status,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'People & Keys');

      if (data.length > 0) {
        const colWidths = Object.keys(data[0]).map((key) => ({
          wch: Math.max(key.length, ...data.map((row) => String(row[key as keyof typeof row] || '').length)),
        }));
        worksheet['!cols'] = colWidths;
      }

      downloadExcel(workbook, 'people_and_keys');
    } finally {
      setExportingReport(null);
    }
  };

  const exportInventory = () => {
    setExportingReport('inventory');
    try {
      const assignedByItem = new Map<string, number>();
      assignments
        .filter((a) => a.status === 'active')
        .forEach((a) => {
          const current = assignedByItem.get(a.keyInventoryItemId) || 0;
          assignedByItem.set(a.keyInventoryItemId, current + a.quantity);
        });

      const data = inventory.map((item) => {
        const assigned = assignedByItem.get(item.id) || 0;
        const available = item.quantity - assigned;

        return {
          'Key Type': getKeyTypeName(item.keyTypeId),
          'Location': getLocationName(item.locationId),
          'Total Quantity': item.quantity,
          'Assigned': assigned,
          'Available': available,
          'Last Updated': new Date(item.lastUpdated).toLocaleDateString(),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Key Inventory');

      if (data.length > 0) {
        const colWidths = Object.keys(data[0]).map((key) => ({
          wch: Math.max(key.length, ...data.map((row) => String(row[key as keyof typeof row] || '').length)),
        }));
        worksheet['!cols'] = colWidths;
      }

      downloadExcel(workbook, 'key_inventory');
    } finally {
      setExportingReport(null);
    }
  };

  const exportTransactions = () => {
    setExportingReport('transactions');
    try {
      const data = transactions.map((t) => {
        const invItem = inventory.find((i) => i.id === t.keyInventoryItemId);

        return {
          'Date': new Date(t.timestamp).toLocaleString(),
          'Type': t.type,
          'Key Type': invItem ? getKeyTypeName(invItem.keyTypeId) : 'Unknown',
          'Location': invItem ? getLocationName(invItem.locationId) : 'Unknown',
          'Quantity': t.quantity,
          'Notes': t.notes || '',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

      if (data.length > 0) {
        const colWidths = Object.keys(data[0]).map((key) => ({
          wch: Math.max(key.length, ...data.map((row) => String(row[key as keyof typeof row] || '').length)),
        }));
        worksheet['!cols'] = colWidths;
      }

      downloadExcel(workbook, 'transaction_history');
    } finally {
      setExportingReport(null);
    }
  };

  const exportFullReport = () => {
    setExportingReport('full-report');
    try {
      const workbook = XLSX.utils.book_new();

      const assignedByItem = new Map<string, number>();
      assignments
        .filter((a) => a.status === 'active')
        .forEach((a) => {
          const current = assignedByItem.get(a.keyInventoryItemId) || 0;
          assignedByItem.set(a.keyInventoryItemId, current + a.quantity);
        });

      const totalKeys = inventory.reduce((sum, item) => sum + item.quantity, 0);
      const totalAssigned = Array.from(assignedByItem.values()).reduce((sum, qty) => sum + qty, 0);
      const activePeople = people.filter((p) => p.status === 'active');
      const peopleWithKeys = new Set(
        assignments.filter((a) => a.status === 'active').map((a) => a.personId)
      ).size;

      const summaryData = [
        { 'Metric': 'Total Keys in System', 'Value': totalKeys },
        { 'Metric': 'Keys Assigned', 'Value': totalAssigned },
        { 'Metric': 'Keys Available', 'Value': totalKeys - totalAssigned },
        { 'Metric': 'Total Locations', 'Value': locations.length },
        { 'Metric': 'Total Key Types', 'Value': keyTypes.length },
        { 'Metric': 'Active People', 'Value': activePeople.length },
        { 'Metric': 'People with Keys', 'Value': peopleWithKeys },
        { 'Metric': 'Report Generated', 'Value': new Date().toLocaleString() },
      ];
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryData), 'Summary');

      const locationsData = locations.map((loc) => ({
        'Name': loc.name,
        'Address': loc.address || '',
        'City': loc.city || '',
        'State': loc.state || '',
        'Zip Code': loc.zipCode || '',
      }));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(locationsData), 'Locations');

      const keyTypesData = keyTypes.map((kt) => ({
        'Name': kt.name,
        'Description': kt.description || '',
      }));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(keyTypesData), 'Key Types');

      const inventoryData = inventory.map((item) => {
        const assigned = assignedByItem.get(item.id) || 0;
        return {
          'Key Type': getKeyTypeName(item.keyTypeId),
          'Location': getLocationName(item.locationId),
          'Total Quantity': item.quantity,
          'Assigned': assigned,
          'Available': item.quantity - assigned,
          'Last Updated': new Date(item.lastUpdated).toLocaleDateString(),
        };
      });
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(inventoryData), 'Key Inventory');

      const peopleData = activePeople.map((person) => ({
        'Name': person.name,
        'Email': person.email || '',
        'Office Location': person.officeLocation || '',
        'Department': person.department || '',
        'Business Phone': person.businessPhone || '',
        'Mobile Phone': person.mobilePhone || '',
        'Location': person.locationId ? getLocationName(person.locationId) : '',
        'Status': person.status,
      }));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(peopleData), 'People');

      const activeAssignments = assignments.filter((a) => a.status === 'active');
      const assignmentsData = activeAssignments.map((a) => {
        const person = people.find((p) => p.id === a.personId);
        const invItem = inventory.find((i) => i.id === a.keyInventoryItemId);
        return {
          'Person': person?.name || 'Unknown',
          'Email': person?.email || '',
          'Key Type': invItem ? getKeyTypeName(invItem.keyTypeId) : 'Unknown',
          'Location': invItem ? getLocationName(invItem.locationId) : 'Unknown',
          'Quantity': a.quantity,
          'Assigned Date': new Date(a.assignedAt).toLocaleDateString(),
          'Assigned By': a.assignedBy,
          'Notes': a.notes || '',
        };
      });
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(assignmentsData), 'Key Assignments');

      const transactionsData = transactions.slice(0, 500).map((t) => {
        const invItem = inventory.find((i) => i.id === t.keyInventoryItemId);
        return {
          'Date': new Date(t.timestamp).toLocaleString(),
          'Type': t.type,
          'Key Type': invItem ? getKeyTypeName(invItem.keyTypeId) : 'Unknown',
          'Location': invItem ? getLocationName(invItem.locationId) : 'Unknown',
          'Quantity': t.quantity,
          'Notes': t.notes || '',
        };
      });
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(transactionsData), 'Transactions');

      downloadExcel(workbook, 'keykeeper_full_report');
    } finally {
      setExportingReport(null);
    }
  };

  const handleExportReport = (optionId: string) => {
    switch (optionId) {
      case 'people-keys':
        exportPeopleAndKeys();
        break;
      case 'inventory':
        exportInventory();
        break;
      case 'transactions':
        exportTransactions();
        break;
      case 'full-report':
        exportFullReport();
        break;
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    setMessage(null);

    try {
      const backup = await backupApi.export();

      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `keykeeper-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Backup exported successfully!' });
    } catch (error) {
      console.error('Export failed:', error);
      setMessage({ type: 'error', text: 'Failed to export backup. Please try again.' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setMessage(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text) as BackupData;

      if (!data.version || !data.data) {
        throw new Error('Invalid backup file format');
      }

      setImportData(data);
      setIsConfirmModalOpen(true);
    } catch (error) {
      console.error('File parse failed:', error);
      setMessage({ type: 'error', text: 'Invalid backup file. Please select a valid KeyKeeper backup.' });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportConfirm = async () => {
    if (!importData) return;

    setIsImporting(true);
    setMessage(null);

    try {
      const result = await backupApi.import(importData);

      setMessage({
        type: 'success',
        text: `Backup restored successfully! Created: ${Object.values(result.results).reduce((sum, r) => sum + r.created, 0)}, Updated: ${Object.values(result.results).reduce((sum, r) => sum + r.updated, 0)}`,
      });

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Import failed:', error);
      setMessage({ type: 'error', text: 'Failed to restore backup. Please try again.' });
    } finally {
      setIsImporting(false);
      setIsConfirmModalOpen(false);
      setImportData(null);
    }
  };

  const handleImportCancel = () => {
    setIsConfirmModalOpen(false);
    setImportData(null);
  };

  // Survey import handlers
  const handleSurveyFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setMessage(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      
      // Convert to JSON
      const data = XLSX.utils.sheet_to_json<Record<string, string>>(sheet, { defval: '' });
      
      if (data.length === 0) {
        setMessage({ type: 'error', text: 'The Excel file appears to be empty or has no data rows.' });
        return;
      }

      setSurveyData(data);
      
      // Preview the data
      setIsPreviewingSurvey(true);
      try {
        const preview = await surveyApi.preview(data);
        setSurveyPreview(preview);
        setIsSurveyPreviewModalOpen(true);
      } catch (error) {
        console.error('Preview error:', error);
        setMessage({ type: 'error', text: `Failed to preview survey: ${error instanceof Error ? error.message : 'Unknown error'}` });
      } finally {
        setIsPreviewingSurvey(false);
      }
    } catch (error) {
      console.error('File read error:', error);
      setMessage({ type: 'error', text: 'Failed to read the Excel file. Please ensure it is a valid .xlsx file.' });
    }

    if (surveyFileInputRef.current) {
      surveyFileInputRef.current.value = '';
    }
  };

  const handleSurveyImportConfirm = async () => {
    if (!surveyData || !currentUser) return;

    setIsImportingSurvey(true);
    setMessage(null);

    try {
      const result = await surveyApi.import(
        surveyData,
        currentUser.name,
        conflictResolutions,
        defaultConflictAction,
        surveyImportLocationId || undefined
      );
      setSurveyImportResults(result);
      setIsSurveyPreviewModalOpen(false);
      setIsSurveyResultsModalOpen(true);
      
      // Refresh data
      dispatch(fetchPeople());
      dispatch(fetchAssignments());
      dispatch(fetchInventory());
    } catch (error) {
      console.error('Import error:', error);
      setMessage({ type: 'error', text: `Failed to import survey: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsImportingSurvey(false);
    }
  };

  const handleSurveyPreviewClose = () => {
    setIsSurveyPreviewModalOpen(false);
    setSurveyPreview(null);
    setSurveyData(null);
    setSurveyImportLocationId('');
    setConflictResolutions({});
    setDefaultConflictAction('merge');
  };

  const handleSurveyResultsClose = () => {
    setIsSurveyResultsModalOpen(false);
    setSurveyImportResults(null);
    setSurveyData(null);
    setSurveyPreview(null);
    setConflictResolutions({});
    setDefaultConflictAction('merge');
  };

  const handleConflictActionChange = (email: string, action: ConflictAction) => {
    setConflictResolutions(prev => ({
      ...prev,
      [email]: action,
    }));
  };

  const applyDefaultToAllConflicts = () => {
    if (!surveyPreview) return;
    const newResolutions: Record<string, ConflictAction> = {};
    surveyPreview.preview.filter(p => p.isConflict).forEach(p => {
      newResolutions[p.email] = defaultConflictAction;
    });
    setConflictResolutions(newResolutions);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 text-secondary-600 dark:text-secondary-400 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Backup & Export</h1>
          <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Export reports, backup, and restore your KeyKeeper data
          </p>
        </div>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg flex items-start ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'
              : message.type === 'info'
              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
          }`}
        >
          {message.type === 'info' ? (
            <InformationCircleIcon className="w-5 h-5 mr-2 flex-shrink-0" />
          ) : (
            <ExclamationTriangleIcon className="w-5 h-5 mr-2 flex-shrink-0" />
          )}
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      {/* Export Reports Section */}
      <Card>
        <div className="flex items-center mb-4">
          <DocumentArrowDownIcon className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">Export Reports</h2>
        </div>
        <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
          Export your data to Excel files for reporting purposes
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {exportOptions.map((option) => (
            <div
              key={option.id}
              className="flex items-start space-x-4 p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg"
            >
              <div className="flex-shrink-0 p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                <option.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                  {option.name}
                </h3>
                <p className="mt-1 text-xs text-secondary-600 dark:text-secondary-400">
                  {option.description}
                </p>
                <Button
                  size="sm"
                  className="mt-3"
                  onClick={() => handleExportReport(option.id)}
                  disabled={exportingReport !== null}
                >
                  {exportingReport === option.id ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="w-3 h-3 mr-2" />
                      Export
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Import from Survey Section */}
      <Card>
        <div className="flex items-center mb-4">
          <DocumentTextIcon className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">Import from Survey</h2>
        </div>
        <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
          Import people and their key assignments from an Excel file.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-4">
          <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-2">Required File Format:</h3>
          <p className="text-sm text-blue-800 dark:text-blue-300 mb-3">
            Your Excel file must have a header row followed by data rows:
          </p>
          <div className="bg-white dark:bg-secondary-800 rounded border border-blue-300 dark:border-blue-700 overflow-hidden mb-3">
            <table className="w-full text-sm">
              <tbody className="text-blue-800 dark:text-blue-300">
                <tr className="bg-blue-100 dark:bg-blue-900/50">
                  <td className="px-2 py-2 text-center font-medium text-blue-600 dark:text-blue-400 border-r border-blue-200 dark:border-blue-700 w-12">Row 1</td>
                  <td className="px-3 py-2 font-semibold text-blue-900 dark:text-blue-200 border-r border-blue-200 dark:border-blue-700">Name</td>
                  <td className="px-3 py-2 font-semibold text-blue-900 dark:text-blue-200 border-r border-blue-200 dark:border-blue-700">Email</td>
                  <td className="px-3 py-2 font-semibold text-blue-900 dark:text-blue-200">Keys</td>
                </tr>
                <tr className="border-t border-blue-200 dark:border-blue-700">
                  <td className="px-2 py-2 text-center font-medium text-blue-600 dark:text-blue-400 border-r border-blue-200 dark:border-blue-700">Row 2</td>
                  <td className="px-3 py-2 border-r border-blue-200 dark:border-blue-700">John Smith</td>
                  <td className="px-3 py-2 border-r border-blue-200 dark:border-blue-700">john.smith@company.com</td>
                  <td className="px-3 py-2">AAA 5, AAA 7, Fork Lift</td>
                </tr>
                <tr className="border-t border-blue-200 dark:border-blue-700">
                  <td className="px-2 py-2 text-center font-medium text-blue-600 dark:text-blue-400 border-r border-blue-200 dark:border-blue-700">Row 3</td>
                  <td className="px-3 py-2 border-r border-blue-200 dark:border-blue-700">Jane Doe</td>
                  <td className="px-3 py-2 border-r border-blue-200 dark:border-blue-700">jane.doe@company.com</td>
                  <td className="px-3 py-2">ABA 8, Golf Cart</td>
                </tr>
              </tbody>
            </table>
          </div>
          <ul className="text-sm text-blue-800 dark:text-blue-300 list-disc list-inside space-y-1">
            <li><strong>Row 1 (Header):</strong> Must contain column names: Name, Email, Keys</li>
            <li><strong>Name:</strong> Person's full name</li>
            <li><strong>Email:</strong> Email address (used to match with Azure AD)</li>
            <li><strong>Keys:</strong> Comma-separated list of key types (1 key per type)</li>
          </ul>
        </div>

        <div className="p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-secondary-900 dark:text-secondary-100 flex items-center">
                <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                Upload Survey File
              </h3>
              <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
                Select an Excel file (.xlsx) with the required format above.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <input
              ref={surveyFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleSurveyFileSelect}
              className="hidden"
            />
            <Button
              onClick={() => surveyFileInputRef.current?.click()}
              disabled={isPreviewingSurvey}
            >
              {isPreviewingSurvey ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                  Select Survey File
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Database Backup Section */}
      <Card>
        <div className="flex items-center mb-4">
          <CloudArrowDownIcon className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">Database Backup</h2>
        </div>

        {!isAdmin ? (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              Only administrators can access backup and restore features.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* What's included */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-2">What's included in the backup:</h3>
              <ul className="text-sm text-blue-800 dark:text-blue-300 grid grid-cols-2 gap-1">
                <li>• Locations</li>
                <li>• Users</li>
                <li>• Key Types</li>
                <li>• Key Inventory</li>
                <li>• Transactions</li>
                <li>• People</li>
                <li>• Key Assignments</li>
              </ul>
            </div>

            {/* Export Section */}
            <div className="p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-secondary-900 dark:text-secondary-100 flex items-center">
                    <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                    Export Backup
                  </h3>
                  <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
                    Download a complete backup of all your KeyKeeper data including locations,
                    users, key types, inventory, transactions, people, and assignments.
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <Button onClick={handleExport} disabled={isExporting}>
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      Download Backup
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Import Section */}
            <div className="p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-secondary-900 dark:text-secondary-100 flex items-center">
                    <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                    Restore from Backup
                  </h3>
                  <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
                    Upload a KeyKeeper backup file to restore your data. Existing records will be
                    updated if they share the same ID.
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImporting}
                >
                  {isImporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                      Restoring...
                    </>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                      Select Backup File
                    </>
                  )}
                </Button>
              </div>
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  <strong>Warning:</strong> Restoring a backup will merge data with existing records.
                  Records with matching IDs will be overwritten.
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Data Summary */}
      <Card>
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">
          Data Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{people.filter(p => p.status === 'active').length}</p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">Active People</p>
          </div>
          <div className="text-center p-4 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{inventory.reduce((sum, i) => sum + i.quantity, 0)}</p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">Total Keys</p>
          </div>
          <div className="text-center p-4 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{locations.length}</p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">Locations</p>
          </div>
          <div className="text-center p-4 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{transactions.length}</p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">Transactions</p>
          </div>
        </div>
      </Card>

      {/* Confirm Import Modal */}
      <Modal
        isOpen={isConfirmModalOpen}
        onClose={handleImportCancel}
        title="Confirm Restore"
        footer={
          <>
            <Button variant="secondary" onClick={handleImportCancel} disabled={isImporting}>
              Cancel
            </Button>
            <Button onClick={handleImportConfirm} disabled={isImporting}>
              {isImporting ? 'Restoring...' : 'Restore Backup'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Are you sure you want to restore this backup?
                </p>
                <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                  This will merge the backup data with your existing data. Records with matching
                  IDs will be overwritten.
                </p>
              </div>
            </div>
          </div>

          {importData && (
            <div className="p-4 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
              <h4 className="text-sm font-medium text-secondary-900 dark:text-secondary-100 mb-2">Backup Details</h4>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between">
                  <dt className="text-secondary-600 dark:text-secondary-400">Version:</dt>
                  <dd className="text-secondary-900 dark:text-secondary-100">{importData.version}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-secondary-600 dark:text-secondary-400">Exported:</dt>
                  <dd className="text-secondary-900 dark:text-secondary-100">
                    {new Date(importData.exportedAt).toLocaleString()}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-secondary-600 dark:text-secondary-400">Exported By:</dt>
                  <dd className="text-secondary-900 dark:text-secondary-100">{importData.exportedBy}</dd>
                </div>
              </dl>
              <div className="mt-3 pt-3 border-t border-secondary-200 dark:border-secondary-600">
                <h5 className="text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-1">Record Counts</h5>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="text-secondary-600 dark:text-secondary-400">Locations: {importData.data.locations?.length || 0}</span>
                  <span className="text-secondary-600 dark:text-secondary-400">Users: {importData.data.users?.length || 0}</span>
                  <span className="text-secondary-600 dark:text-secondary-400">Key Types: {importData.data.keyTypes?.length || 0}</span>
                  <span className="text-secondary-600 dark:text-secondary-400">Inventory: {importData.data.inventory?.length || 0}</span>
                  <span className="text-secondary-600 dark:text-secondary-400">Transactions: {importData.data.transactions?.length || 0}</span>
                  <span className="text-secondary-600 dark:text-secondary-400">People: {importData.data.people?.length || 0}</span>
                  <span className="text-secondary-600 dark:text-secondary-400">Assignments: {importData.data.keyAssignments?.length || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Survey Preview Modal */}
      <Modal
        isOpen={isSurveyPreviewModalOpen}
        onClose={handleSurveyPreviewClose}
        title="Survey Import Preview"
        footer={
          <>
            <Button variant="secondary" onClick={handleSurveyPreviewClose} disabled={isImportingSurvey}>
              Cancel
            </Button>
            <Button
              onClick={handleSurveyImportConfirm}
              disabled={isImportingSurvey}
            >
              {isImportingSurvey ? 'Importing...' : 'Import Survey'}
            </Button>
          </>
        }
      >
        {surveyPreview && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="p-4 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
              <h4 className="text-sm font-medium text-secondary-900 dark:text-secondary-100 mb-2">
                Survey Data Summary
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-secondary-600 dark:text-secondary-400">
                  Total Respondents:
                </div>
                <div className="text-secondary-900 dark:text-secondary-100 font-medium">
                  {surveyPreview.rowCount}
                </div>
                <div className="text-secondary-600 dark:text-secondary-400">
                  Existing People:
                </div>
                <div className="text-secondary-900 dark:text-secondary-100 font-medium">
                  {surveyPreview.conflictCount} <span className="text-secondary-500 text-xs">(conflicts)</span>
                </div>
                <div className="text-secondary-600 dark:text-secondary-400">
                  New People:
                </div>
                <div className="text-secondary-900 dark:text-secondary-100 font-medium">
                  {surveyPreview.rowCount - surveyPreview.conflictCount}
                </div>
              </div>
            </div>

            {/* Location Selection (Optional) */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Assign to Location <span className="text-secondary-500 text-xs">(optional)</span>
              </label>
              <select
                value={surveyImportLocationId}
                onChange={(e) => setSurveyImportLocationId(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">No location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                If no location is selected, keys will only be assigned if inventory already exists.
              </p>
            </div>

            {/* Conflict Resolution Section */}
            {surveyPreview.conflictCount > 0 && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        {surveyPreview.conflictCount} Existing People Found
                      </h4>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        Choose how to handle people who already exist in the system
                      </p>
                    </div>
                  </div>
                </div>

                {/* Default Action */}
                <div className="mb-3 p-3 bg-white dark:bg-secondary-800 rounded-lg">
                  <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Default action for all conflicts:
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => setDefaultConflictAction('merge')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        defaultConflictAction === 'merge'
                          ? 'bg-blue-600 text-white'
                          : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
                      }`}
                    >
                      Merge (add new keys)
                    </button>
                    <button
                      onClick={() => setDefaultConflictAction('overwrite')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        defaultConflictAction === 'overwrite'
                          ? 'bg-amber-600 text-white'
                          : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
                      }`}
                    >
                      Overwrite (replace keys)
                    </button>
                    <button
                      onClick={() => setDefaultConflictAction('skip')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        defaultConflictAction === 'skip'
                          ? 'bg-secondary-600 text-white'
                          : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
                      }`}
                    >
                      Skip (no changes)
                    </button>
                  </div>
                  <button
                    onClick={applyDefaultToAllConflicts}
                    className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Apply to all conflicts
                  </button>
                </div>

                {/* Conflict Details */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {surveyPreview.preview.filter(p => p.isConflict).map((row, idx) => {
                    const currentAction = conflictResolutions[row.email] || defaultConflictAction;
                    const surveyKeys = row.keys.filter(k => k.matched !== null).map(k => k.matched as string);
                    const existingKeys = row.existingAssignments.map(a => a.keyTypeName);
                    
                    return (
                      <div key={idx} className="p-3 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-600">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                              {row.existingPerson?.name || row.email}
                            </p>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                              {row.email}
                            </p>
                          </div>
                          <select
                            value={currentAction}
                            onChange={(e) => handleConflictActionChange(row.email, e.target.value as ConflictAction)}
                            className="text-xs px-2 py-1 border rounded border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
                          >
                            <option value="merge">Merge</option>
                            <option value="overwrite">Overwrite</option>
                            <option value="skip">Skip</option>
                          </select>
                        </div>
                        
                        {/* Comparison View */}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="font-medium text-secondary-600 dark:text-secondary-400 mb-1">Current Keys:</p>
                            <div className="flex flex-wrap gap-1">
                              {existingKeys.length > 0 ? existingKeys.map((key, i) => (
                                <span key={i} className="px-1.5 py-0.5 bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 rounded">
                                  {key}
                                </span>
                              )) : (
                                <span className="text-secondary-400 italic">None</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-secondary-600 dark:text-secondary-400 mb-1">Survey Keys:</p>
                            <div className="flex flex-wrap gap-1">
                              {surveyKeys.length > 0 ? surveyKeys.map((key, i) => (
                                <span key={i} className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                                  {key}
                                </span>
                              )) : (
                                <span className="text-secondary-400 italic">None</span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Result Preview */}
                        <div className="mt-2 pt-2 border-t border-secondary-100 dark:border-secondary-700">
                          <p className="font-medium text-secondary-600 dark:text-secondary-400 mb-1 text-xs">
                            Result ({currentAction}):
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {currentAction === 'skip' ? (
                              <span className="text-xs text-secondary-400 italic">No changes</span>
                            ) : currentAction === 'overwrite' ? (
                              surveyKeys.length > 0 ? surveyKeys.map((key, i) => (
                                <span key={i} className="px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                                  {key}
                                </span>
                              )) : (
                                <span className="text-xs text-secondary-400 italic">No keys</span>
                              )
                            ) : (
                              // Merge: combine existing + new
                              [...new Set([...existingKeys, ...surveyKeys])].map((key, i) => (
                                <span 
                                  key={i} 
                                  className={`px-1.5 py-0.5 text-xs rounded ${
                                    surveyKeys.includes(key) && !existingKeys.includes(key)
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                      : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300'
                                  }`}
                                >
                                  {key}{surveyKeys.includes(key) && !existingKeys.includes(key) ? ' (new)' : ''}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Unmatched Keys Warning */}
            {surveyPreview.preview.some(p => p.unmatchedKeys.length > 0) && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Some keys could not be matched
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      Unmatched: {[...new Set(surveyPreview.preview.flatMap(p => p.unmatchedKeys))].join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* New People Preview */}
            {surveyPreview.preview.filter(p => !p.isConflict).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-secondary-900 dark:text-secondary-100 mb-2">
                  New People ({surveyPreview.preview.filter(p => !p.isConflict).length})
                </h4>
                <div className="border border-secondary-200 dark:border-secondary-600 rounded-lg overflow-hidden">
                  <div className="max-h-48 overflow-y-auto">
                    <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-600">
                      <thead className="bg-secondary-50 dark:bg-secondary-700 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                            Email
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                            Keys to Assign
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-secondary-200 dark:divide-secondary-600">
                        {surveyPreview.preview.filter(p => !p.isConflict).slice(0, 10).map((row, idx) => (
                          <tr key={idx} className="bg-white dark:bg-secondary-800">
                            <td className="px-3 py-2 text-sm text-secondary-900 dark:text-secondary-100">
                              {row.email}
                              {row.name && row.name !== row.email && (
                                <span className="block text-xs text-secondary-500 dark:text-secondary-400">
                                  {row.name}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm">
                              <div className="flex flex-wrap gap-1">
                                {row.keys.filter(k => k.matched).map((key, keyIdx) => (
                                  <span
                                    key={keyIdx}
                                    className="inline-block px-2 py-0.5 text-xs rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                                  >
                                    {key.matched}
                                  </span>
                                ))}
                                {row.keys.filter(k => k.matched).length === 0 && (
                                  <span className="text-secondary-400 dark:text-secondary-500 text-xs italic">
                                    No matched keys
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {surveyPreview.preview.filter(p => !p.isConflict).length > 10 && (
                  <p className="mt-2 text-xs text-secondary-500 dark:text-secondary-400">
                    ...and {surveyPreview.preview.filter(p => !p.isConflict).length - 10} more new people
                  </p>
                )}
              </div>
            )}

            {/* Info */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <InformationCircleIcon className="w-4 h-4 inline mr-1" />
                Each respondent will be looked up in Azure AD to get their full profile information.
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Survey Results Modal */}
      <Modal
        isOpen={isSurveyResultsModalOpen}
        onClose={handleSurveyResultsClose}
        title="Survey Import Complete"
        footer={
          <Button onClick={handleSurveyResultsClose}>
            Close
          </Button>
        }
      >
        {surveyImportResults && (
          <div className="space-y-4">
            {/* Stats Summary */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400 mr-2" />
                <h4 className="text-sm font-medium text-green-900 dark:text-green-100">
                  Import Successful
                </h4>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <div className="p-2 bg-white dark:bg-secondary-800 rounded">
                  <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                    {surveyImportResults.stats.created}
                  </p>
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Created</p>
                </div>
                <div className="p-2 bg-white dark:bg-secondary-800 rounded">
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {surveyImportResults.stats.updated}
                  </p>
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Updated</p>
                </div>
                <div className="p-2 bg-white dark:bg-secondary-800 rounded">
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">
                    {surveyImportResults.stats.azureAdMatched}
                  </p>
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Azure AD Matched</p>
                </div>
                <div className="p-2 bg-white dark:bg-secondary-800 rounded">
                  <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                    {surveyImportResults.stats.totalKeysAssigned}
                  </p>
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Keys Assigned</p>
                </div>
              </div>
            </div>

            {/* Errors if any */}
            {surveyImportResults.stats.errors > 0 && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-start">
                  <XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {surveyImportResults.stats.errors} error(s) occurred
                    </p>
                    <ul className="mt-1 text-xs text-red-700 dark:text-red-300 list-disc list-inside">
                      {surveyImportResults.results
                        .filter(r => r.status === 'error')
                        .slice(0, 5)
                        .map((r, idx) => (
                          <li key={idx}>{r.email}: {r.error}</li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Results List */}
            <div>
              <h4 className="text-sm font-medium text-secondary-900 dark:text-secondary-100 mb-2">
                Import Details
              </h4>
              <div className="border border-secondary-200 dark:border-secondary-600 rounded-lg overflow-hidden">
                <div className="max-h-60 overflow-y-auto">
                  <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-600">
                    <thead className="bg-secondary-50 dark:bg-secondary-700 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                          Person
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                          Keys
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-200 dark:divide-secondary-600">
                      {surveyImportResults.results.map((result, idx) => (
                        <tr key={idx} className="bg-white dark:bg-secondary-800">
                          <td className="px-3 py-2 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-secondary-900 dark:text-secondary-100">
                                {result.name}
                              </span>
                              {result.azureAdMatched && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                                  AD
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-secondary-500 dark:text-secondary-400">
                              {result.email}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm">
                            <span
                              className={`inline-block px-2 py-0.5 text-xs rounded ${
                                result.status === 'created'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                  : result.status === 'updated'
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                  : result.status === 'error'
                                  ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                  : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400'
                              }`}
                            >
                              {result.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-secondary-600 dark:text-secondary-400">
                            {result.keysAssigned.length > 0 ? (
                              <span className="text-xs">{result.keysAssigned.join(', ')}</span>
                            ) : (
                              <span className="text-xs italic">None</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
